import {
  Apple,
  Bell,
  Building2,
  Calendar,
  CheckCircle,
  ChevronRight,
  Download,
  FileText,
  Info,
  Laptop,
  LifeBuoy,
  Loader2,
  Monitor,
  Package,
  Printer,
  Settings2,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModuleGate } from "../../components/auth/ModuleGate";
import { useNotificationPreferences } from "../../hooks/useNotificationPreferences";

export function NotificationSettingsSection() {
  const { preferences, isLoading, isSaved, updatePreferences } =
    useNotificationPreferences();

  if (isLoading) {
    return (
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Notifications
            </h2>
            <p className="text-sm text-slate-500">
              Loading your alert preferences
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-16 text-sm text-slate-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading preferences...
        </div>
      </section>
    );
  }

  return (
    <section
      id="notifications"
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Notifications
            </h2>
            <p className="text-sm text-slate-500">
              Choose when inventory alerts should appear
            </p>
          </div>
        </div>
        {isSaved && (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            <CheckCircle className="h-3.5 w-3.5" />
            Saved automatically
          </span>
        )}
      </div>

      <div className="divide-y divide-slate-100 px-5 sm:px-6">
        {/* Low Stock Alerts */}
        <div className="flex flex-col gap-4 py-5 sm:flex-row sm:items-start">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <Package className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">
                  Low stock alerts
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Alert staff before fast-moving products run out.
                </p>
              </div>
              <label className="relative mt-0.5 inline-flex shrink-0 cursor-pointer items-center">
                <input
                  type="checkbox"
                  aria-label="Enable low stock alerts"
                  checked={preferences.lowStockEnabled}
                  onChange={(e) =>
                    updatePreferences({ lowStockEnabled: e.target.checked })
                  }
                  className="sr-only peer"
                />
                <span className="h-6 w-11 rounded-full bg-slate-200 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:content-[''] peer-checked:bg-teal-600 peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-teal-500 peer-focus-visible:ring-offset-2" />
              </label>
            </div>
            {preferences.lowStockEnabled && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5">
                <label
                  htmlFor="low-stock-threshold"
                  className="text-sm text-slate-600"
                >
                  Notify when stock drops below
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="low-stock-threshold"
                    type="number"
                    min={1}
                    max={1000}
                    value={preferences.lowStockThreshold}
                    onChange={(e) =>
                      updatePreferences({
                        lowStockThreshold: Math.max(
                          1,
                          parseInt(e.target.value) || 1,
                        ),
                      })
                    }
                    className="h-9 w-20 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  />
                  <span className="text-sm text-slate-500">units</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stock Expiry Alerts */}
        <div className="flex flex-col gap-4 py-5 sm:flex-row sm:items-start">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
            <Calendar className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900">Expiry alerts</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Get advance notice before batches reach their expiry date.
                </p>
              </div>
              <label className="relative mt-0.5 inline-flex shrink-0 cursor-pointer items-center">
                <input
                  type="checkbox"
                  aria-label="Enable stock expiry alerts"
                  checked={preferences.stockExpiryEnabled}
                  onChange={(e) =>
                    updatePreferences({ stockExpiryEnabled: e.target.checked })
                  }
                  className="sr-only peer"
                />
                <span className="h-6 w-11 rounded-full bg-slate-200 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:content-[''] peer-checked:bg-teal-600 peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-teal-500 peer-focus-visible:ring-offset-2" />
              </label>
            </div>
            {preferences.stockExpiryEnabled && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5">
                <label
                  htmlFor="stock-expiry-days"
                  className="text-sm text-slate-600"
                >
                  Notify before expiry
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="stock-expiry-days"
                    type="number"
                    min={1}
                    max={365}
                    value={preferences.stockExpiryDays}
                    onChange={(e) =>
                      updatePreferences({
                        stockExpiryDays: Math.max(
                          1,
                          parseInt(e.target.value) || 1,
                        ),
                      })
                    }
                    className="h-9 w-20 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  />
                  <span className="text-sm text-slate-500">days</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-3 text-xs text-slate-500 sm:px-6">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
        Changes save automatically. Triggered alerts appear in the notification
        center for this account.
      </div>
    </section>
  );
}

// Detect user's operating system
function detectOS(): "macos" | "windows" | "linux" | "unknown" {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform.toLowerCase();

  if (platform.includes("mac") || userAgent.includes("mac")) {
    return "macos";
  }
  if (platform.includes("win") || userAgent.includes("windows")) {
    return "windows";
  }
  if (platform.includes("linux") || userAgent.includes("linux")) {
    return "linux";
  }
  return "unknown";
}

// R2 CDN URL for direct downloads
const R2_CDN_URL = "https://cdn-r2.medicorepay.com";

// Desktop app download URLs - Direct downloads from R2
const DOWNLOAD_URLS = {
  macos: {
    url: `${R2_CDN_URL}/downloads/MediCorePay_macos.dmg`,
    filename: "MediCorePay.dmg",
    size: "~6 MB",
    requirements: "macOS 10.15 (Catalina) or later",
    available: true,
  },
  windows: {
    url: `${R2_CDN_URL}/downloads/MediCorePay_windows.msi`,
    filename: "MediCorePay-Setup.msi",
    size: "~8 MB",
    requirements: "Windows 10 or later (64-bit)",
    available: true,
  },
  linux: {
    url: `${R2_CDN_URL}/downloads/MediCorePay_linux.AppImage`,
    filename: "MediCorePay.AppImage",
    size: "~10 MB",
    requirements: "Ubuntu 20.04+ / Debian 10+ / Fedora 35+",
    available: false, // Will be available after cross-platform build
  },
};

const OS_INFO = {
  macos: {
    name: "macOS",
    icon: Apple,
    color: "bg-gray-800",
  },
  windows: {
    name: "Windows",
    icon: Monitor,
    color: "bg-blue-600",
  },
  linux: {
    name: "Linux",
    icon: Laptop,
    color: "bg-orange-600",
  },
  unknown: {
    name: "Desktop",
    icon: Monitor,
    color: "bg-gray-600",
  },
};

function DesktopDownloadCard() {
  const [detectedOS, setDetectedOS] = useState<
    "macos" | "windows" | "linux" | "unknown"
  >("unknown");
  const [showAllPlatforms, setShowAllPlatforms] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setDetectedOS(detectOS());
  }, []);

  const primaryOS = detectedOS !== "unknown" ? detectedOS : "macos"; // Default to macOS since it's available
  const OSIcon = OS_INFO[primaryOS].icon;
  const downloadInfo = DOWNLOAD_URLS[primaryOS as keyof typeof DOWNLOAD_URLS];

  // Direct download function - creates a temporary link and clicks it
  const handleDownload = (os: "macos" | "windows" | "linux") => {
    const info = DOWNLOAD_URLS[os];

    if (!info.available) {
      alert(
        `${OS_INFO[os].name} version is coming soon! Please check back later.`,
      );
      return;
    }

    setDownloading(true);

    // Create a temporary anchor element for direct download
    const link = document.createElement("a");
    link.href = info.url;
    link.download = info.filename; // This triggers download instead of navigation
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Reset downloading state after a short delay
    setTimeout(() => setDownloading(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
      <div className="flex items-start gap-4">
        <div
          className={`p-3 rounded-xl ${OS_INFO[primaryOS].color} text-white`}
        >
          <Download className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Download Desktop App
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Get the MediCorePay desktop app for faster performance, offline
            support, and hardware integration (printers, barcode scanners).
          </p>

          {/* Primary download button */}
          <div className="mb-4">
            <button
              onClick={() =>
                handleDownload(primaryOS as "macos" | "windows" | "linux")
              }
              disabled={downloading || !downloadInfo?.available}
              className={`inline-flex items-center gap-2 px-5 py-2.5 ${
                downloadInfo?.available
                  ? `${OS_INFO[primaryOS].color} hover:opacity-90`
                  : "bg-gray-400 cursor-not-allowed"
              } text-white rounded-lg transition-opacity font-medium`}
            >
              <OSIcon className="w-5 h-5" />
              {downloading
                ? "Downloading..."
                : downloadInfo?.available
                  ? `Download for ${OS_INFO[primaryOS].name}`
                  : `${OS_INFO[primaryOS].name} Coming Soon`}
              {downloadInfo?.available && <Download className="w-4 h-4 ml-1" />}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              {downloadInfo?.available
                ? `${downloadInfo?.filename} • ${downloadInfo?.size}`
                : "Build in progress, please try macOS version"}
            </p>
          </div>

          {/* Desktop app benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {[
              "Works offline",
              "Faster performance",
              "Printer integration",
              "Barcode scanner support",
              "Auto-updates",
              "Local data backup",
            ].map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-2 text-sm text-gray-600"
              >
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                {benefit}
              </div>
            ))}
          </div>

          {/* System requirements */}
          <div className="flex items-start gap-2 text-xs text-gray-500 mb-4 bg-white/50 rounded-lg p-3">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>System Requirements:</strong> {downloadInfo?.requirements}
            </span>
          </div>

          {/* Other platforms */}
          <div>
            <button
              onClick={() => setShowAllPlatforms(!showAllPlatforms)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {showAllPlatforms
                ? "Hide other platforms"
                : "Download for other platforms"}
            </button>

            {showAllPlatforms && (
              <div className="mt-3 flex flex-wrap gap-2">
                {(["macos", "windows", "linux"] as const)
                  .filter((os) => os !== primaryOS)
                  .map((os) => {
                    const Icon = OS_INFO[os].icon;
                    const info = DOWNLOAD_URLS[os];
                    return (
                      <button
                        key={os}
                        onClick={() => handleDownload(os)}
                        disabled={!info.available}
                        className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium ${
                          info.available
                            ? "bg-white border-gray-200 hover:bg-gray-50 text-gray-700"
                            : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {OS_INFO[os].name}
                        {!info.available && (
                          <span className="text-xs">(Soon)</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const settingsOptions: Array<{
    label: string;
    description: string;
    icon: LucideIcon;
    path: string;
    tone: string;
    iconTone: string;
  }> = [
    {
      label: "Profile",
      description: "Review your identity, contact details and store access.",
      icon: User,
      path: "/profile",
      tone: "hover:border-sky-200 hover:bg-sky-50/30",
      iconTone: "bg-sky-50 text-sky-600",
    },
    {
      label: "Pharmacy Information",
      description:
        "Manage store identity, GSTIN, Drug Licence and FSSAI details.",
      icon: Building2,
      path: "/settings/pharmacy-information",
      tone: "hover:border-cyan-200 hover:bg-cyan-50/30",
      iconTone: "bg-cyan-50 text-cyan-700",
    },
    {
      label: "Notifications",
      description: "Control low-stock and batch-expiry alert thresholds.",
      icon: Bell,
      path: "/settings/notifications",
      tone: "hover:border-teal-200 hover:bg-teal-50/30",
      iconTone: "bg-teal-50 text-teal-700",
    },
    {
      label: "Printers",
      description: "Configure receipt, label and report printing devices.",
      icon: Printer,
      path: "/settings/printers",
      tone: "hover:border-indigo-200 hover:bg-indigo-50/30",
      iconTone: "bg-indigo-50 text-indigo-600",
    },
    {
      label: "Invoices",
      description: "Customize invoice columns, layout and appearance.",
      icon: FileText,
      path: "/settings/invoice-customization",
      tone: "hover:border-emerald-200 hover:bg-emerald-50/30",
      iconTone: "bg-emerald-50 text-emerald-600",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl animate-fade-in space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-teal-700">
            <Settings2 className="h-4 w-4" />
            Workspace preferences
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Settings
          </h1>
          <p className="mt-1 text-sm text-slate-500 sm:text-base">
            Keep account, alerts, printing and invoices organized in one place.
          </p>
        </div>
        <a
          href="mailto:support@medicorepay.com"
          className="inline-flex h-10 w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-teal-200 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
        >
          <LifeBuoy className="h-4 w-4" />
          Get help
        </a>
      </header>

      <section aria-label="Settings options">
        <div className="grid gap-4 sm:grid-cols-2">
          {settingsOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.label}
                type="button"
                onClick={() => navigate(option.path)}
                className={`group flex min-h-40 flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 sm:p-6 ${option.tone}`}
              >
                <div className="flex w-full items-start justify-between gap-4">
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-xl ${option.iconTone}`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <ChevronRight className="h-5 w-5 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-teal-600" />
                </div>
                <h2 className="mt-5 text-base font-semibold text-slate-900">
                  {option.label}
                </h2>
                <p className="mt-1 max-w-md text-sm leading-5 text-slate-500">
                  {option.description}
                </p>
                <span className="mt-auto pt-4 text-xs font-semibold text-slate-400 transition group-hover:text-teal-700">
                  Open settings
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          <ModuleGate requiredModule="DESKTOP_APP">
            <DesktopDownloadCard />
          </ModuleGate>
        </div>

        <div className="mt-6 flex flex-col gap-1 border-t border-slate-200 pt-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>MediCorePay Web v{import.meta.env.VITE_APP_VERSION || "1.0.0"}</p>
          <p>Preferences are securely synced to your account.</p>
        </div>
      </section>
    </div>
  );
}

export default SettingsPage;
